// NetworkProvider.js
import { createContext, useState, useEffect } from "react";

export const NetworkContext = createContext();

export const NetworkProvider = ({ children }) => {
  const [internetAvailable, setInternetAvailable] = useState(navigator.onLine);

  // Real internet check by pinging Google (or your own server).
  const checkInternetAccess = async () => {
    // navigator.onLine === false is the only RELIABLE "offline" signal. If the
    // browser says we're online, a failed/slow ping does NOT mean offline — the
    // ping to google can be blocked/slow on corporate/VPN networks, and letting
    // it flip us to offline made internetAvailable flap every ~10s, which blinked
    // (and intermittently disabled) every control gated on it, e.g. the call
    // icon. So never go offline while navigator.onLine is true.
    if (!navigator.onLine) {
      setInternetAvailable(false);
      return;
    }
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000); // 3s timeout

      await fetch("https://www.google.com/favicon.ico", {
        method: "HEAD",
        mode: "no-cors", // this prevents CORS errors but still triggers the request
        signal: controller.signal,
      });

      clearTimeout(timeout);
      setInternetAvailable(true);
    } catch (err) {
      // Ping failed but the browser is online — stay online rather than flapping.
      setInternetAvailable(true);
    }
  };

  useEffect(() => {
    const handleOnline = () => checkInternetAccess();
    const handleOffline = () => setInternetAvailable(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial check
    checkInternetAccess();

    // Periodically check every 10 seconds
    const interval = setInterval(checkInternetAccess, 10000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  return (
    <NetworkContext.Provider value={{ internetAvailable }}>
      {children}
    </NetworkContext.Provider>
  );
};
